import { useEffect, useRef } from "react";
import axios from "axios";

// Live DOM translation for the (German-authored) public landing page.
// Text nodes are swapped in place; translations come from a cached backend endpoint.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const LANDING_LANGS = [
  { code: "de", label: "DE" }, { code: "en", label: "EN" }, { code: "it", label: "IT" }, { code: "tr", label: "TR" },
  { code: "ru", label: "RU" }, { code: "es", label: "ES" }, { code: "fr", label: "FR" }, { code: "pl", label: "PL" },
  { code: "sr", label: "SR" }, { code: "hr", label: "HR" }, { code: "sq", label: "SQ" }, { code: "sl", label: "SL" }, { code: "pt", label: "PT" },
  { code: "cs", label: "CS" }, { code: "hu", label: "HU" }, { code: "hi", label: "HI" }, { code: "ms", label: "MS" }, { code: "nl", label: "NL" }, { code: "zh", label: "ZH" },
];
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "SVG", "PATH", "CANVAS"]);
const HAS_WORD = /\p{L}{2,}/u;
const ATTRS = ["placeholder", "title", "aria-label"];

const memo = {};
const loadMemo = (lang) => {
  if (!memo[lang]) {
    try { memo[lang] = JSON.parse(localStorage.getItem(`landing_tx_${lang}`) || "{}"); } catch { memo[lang] = {}; }
  }
  return memo[lang];
};
const saveMemo = (lang) => { try { localStorage.setItem(`landing_tx_${lang}`, JSON.stringify(memo[lang])); } catch { /* quota */ } };

export const initialLandingLang = () => {
  const saved = localStorage.getItem("landing_lang");
  if (saved && LANDING_LANGS.some((l) => l.code === saved)) return saved;
  const nav = (navigator.language || "de").slice(0, 2).toLowerCase();
  return LANDING_LANGS.some((l) => l.code === nav) ? nav : "de";
};

const skip = (el) => {
  for (let e = el; e && e !== document.body; e = e.parentElement) {
    if (SKIP_TAGS.has(e.tagName?.toUpperCase()) || e.getAttribute?.("translate") === "no" || e.isContentEditable) return true;
  }
  return false;
};

export function useLandingTranslator(lang, rootRef) {
  const state = useRef({ texts: new Map(), attrs: new Map(), pending: new Set(), timer: null, lang });

  useEffect(() => {
    const st = state.current;
    st.lang = lang;
    const root = rootRef?.current || document.body;
    if (!rootRef) document.documentElement.lang = lang;
    const dict = lang === "de" ? null : loadMemo(lang);

    const applyText = (node) => {
      const rec = st.texts.get(node);
      const cur = node.nodeValue;
      let orig = rec?.orig;
      if (!rec || (cur !== rec.orig && cur !== rec.shown)) orig = cur; // React re-rendered new German text
      const trimmed = orig.trim();
      if (!trimmed || !HAS_WORD.test(trimmed)) return;
      let shown = orig;
      if (dict) {
        const t = dict[trimmed];
        if (t) shown = orig.replace(trimmed, t); else st.pending.add(trimmed);
      }
      st.texts.set(node, { orig, shown });
      if (node.nodeValue !== shown) node.nodeValue = shown;
    };

    const applyAttrs = (el) => {
      ATTRS.forEach((a) => {
        if (!el.hasAttribute?.(a)) return;
        const key = `${a}`;
        const map = st.attrs.get(el) || {};
        const cur = el.getAttribute(a);
        const rec = map[key];
        const orig = rec && (cur === rec.orig || cur === rec.shown) ? rec.orig : cur;
        if (!HAS_WORD.test(orig)) return;
        let shown = orig;
        if (dict) { const t = dict[orig.trim()]; if (t) shown = t; else st.pending.add(orig.trim()); }
        map[key] = { orig, shown };
        st.attrs.set(el, map);
        if (cur !== shown) el.setAttribute(a, shown);
      });
    };

    const walk = (root) => {
      if (root.nodeType === 3) { if (!skip(root.parentElement)) applyText(root); return; }
      if (root.nodeType !== 1 || skip(root)) return;
      applyAttrs(root);
      const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
      for (let n = w.nextNode(); n; n = w.nextNode()) {
        if (n.nodeType === 1) { if (!skip(n)) applyAttrs(n); } else if (!skip(n.parentElement)) applyText(n);
      }
    };

    const flush = () => {
      st.timer = null;
      if (!dict || !st.pending.size || st.lang !== lang) return;
      const texts = [...st.pending].slice(0, 400);
      texts.forEach((t) => st.pending.delete(t));
      axios.post(`${API}/public/landing-translate`, { lang, texts })
        .then((r) => {
          Object.assign(dict, r.data?.map || {});
          saveMemo(lang);
          if (st.lang === lang) walk(root);
        })
        .catch(() => { /* keep German */ });
    };
    const schedule = () => { if (dict && st.pending.size && !st.timer) st.timer = setTimeout(flush, 250); };

    walk(root);
    schedule();
    const obs = new MutationObserver((muts) => {
      muts.forEach((m) => {
        if (m.type === "characterData") { if (!skip(m.target.parentElement)) applyText(m.target); }
        else if (m.type === "attributes") { if (!skip(m.target)) applyAttrs(m.target); }
        else m.addedNodes.forEach(walk);
      });
      schedule();
    });
    obs.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    return () => { obs.disconnect(); if (st.timer) { clearTimeout(st.timer); st.timer = null; } };
  }, [lang, rootRef]);
}
